import {omit, last} from 'lodash';
import {useCallback, useContext, useEffect, useMemo} from 'react';
import Div100vh from 'react-div-100vh';
import {createHtmlPortalNode, InPortal, OutPortal} from 'react-reverse-portal';

import {Loader} from 'components';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {ClientDevice, SessionActivityTextChatData} from 'interfaces';
import {UrlService} from 'services';
import {DefaultParticipant} from 'types';
import {sessionUtils} from 'utils';

import {ClientSessionStartupContainer} from '../SessionStartupContainer';
import {ClientSessionTextChat} from '../TextChat';
import {ClientView} from '../types';
import {WithClientSessionParams, withClientSessionParamsFromUrl} from '../withClientSessionParamsFromUrl';

import {AfterHoursView} from './AfterHoursView';
import {AgentsUnavailableView} from './AgentsUnavailableView';
import {AvatarView} from './AvatarView';
import {CallingView} from './CallingView';
import {FullScreenAsyncRequest} from './FullScreenAsyncRequest';
import {FullScreenTextChat} from './FullScreenTextChat';
import {InSessionView} from './InSessionView';
import {OnHoldView} from './OnHoldView';
import {ParentWindowNotifier} from './ParentWindowNotifier';
import {PreSessionFlowView} from './PreSessionFlowView';
import {usePreSessionStepOptions} from './PreSessionStepCard';
import {RequestAppointmentView} from './RequestAppointmentView';
import {SessionEndedView} from './SessionEndedView';
import {SipView} from './SipView';
import {WaitingForAgentView} from './WaitingForAgentView';

import './styles.scss';

const PARENT_WINDOW_MESSAGE = {
    HANDLE_BIOMETRIC_VALIDATION_RESULT: 'hbvr',
    CLOSE_WIDGET: 'ezs-cw',
};

type Props = {
    isClientConnect?: boolean;
    conversationOnly?: boolean;
} & WithClientSessionParams;

export const ClientConferenceWebContent: React.FC<Props> = ({isClientConnect, clientSessionParams}) => {
    const textChatPortalNode = useMemo(() => createHtmlPortalNode(), []);

    const {session, textChatActivity, isEnded, downloadFileDisabled} = useClientSession();
    const {
        currentView, textChatThreads, textChatInteractiveChatState, waitingStartSessionFromChildren, device, voiceInput,
        textChatInputDisabled, endLocalSession, handleUploadSelfieVerificationResult, startSessionWithParams,
        startSessionConnect, onSubmitMessage, onFileDownload, onChangeTextChatActivityState
    } = useContext(ClientContext);

    const onReceiveMessage = useCallback((event) => {
        const message = event.data;
        if (message.type === PARENT_WINDOW_MESSAGE.HANDLE_BIOMETRIC_VALIDATION_RESULT) {
            const isSuccess = message.value === true;
            handleUploadSelfieVerificationResult(isSuccess);
        } else if (message.type === PARENT_WINDOW_MESSAGE.CLOSE_WIDGET) {
            if (!isEnded) {
                endLocalSession();
            }
        }
    }, [endLocalSession, handleUploadSelfieVerificationResult, isEnded]);

    const renderTextChat = useCallback((props) =>
        <OutPortal node={textChatPortalNode} className='vs-text-chat-portal-container' {...props} />,
    [textChatPortalNode]);

    const recentClientMessage = useMemo(() => last(sessionUtils.getTextChatActivityMessages(
        omit(textChatThreads, [DefaultParticipant.Chatbot, DefaultParticipant.System])
    )), [textChatThreads]);

    // Mirrors the backend's own willSpeak check (session-activity.service.ts): a reply is only spoken when the
    // activity supports voice replies AND the turn it answers was itself spoken. Reacting to the voice reply
    // actually starting would be too late — the text message (and its ding) is broadcast well before TTS synthesis
    // even starts, so this has to predict from the turn that triggered the reply, not from playback state.
    const isVoiceModeActive = useMemo(() => !!(
        (textChatActivity?.data as SessionActivityTextChatData)?.streamAudio?.voiceReply && recentClientMessage?.meta?.isTranscription
    ), [textChatActivity, recentClientMessage]);

    // The step in progress is docked above the input, so a photo or a vendor frame never takes the client
    // out of the conversation.
    const preSessionStepPanel = usePreSessionStepOptions();

    const renderTextChatPortal = useCallback(() => (
        textChatActivity &&
            <InPortal node={textChatPortalNode}>
                <ClientSessionTextChat
                    session={session}
                    threads={textChatThreads}
                    interactiveChatState={textChatInteractiveChatState}
                    downloadFileDisabled={downloadFileDisabled}
                    voiceInput={voiceInput}
                    inputDisabled={textChatInputDisabled}
                    disableNewMessageSound={isVoiceModeActive}
                    dockedPanel={preSessionStepPanel}
                    onSubmitMessage={onSubmitMessage}
                    onFileDownload={onFileDownload}
                    onChangeState={onChangeTextChatActivityState}
                />
            </InPortal>
    ), [textChatActivity, textChatPortalNode, session, textChatThreads, textChatInteractiveChatState, downloadFileDisabled, voiceInput,
        textChatInputDisabled, isVoiceModeActive, preSessionStepPanel, onSubmitMessage, onFileDownload, onChangeTextChatActivityState]);

    useEffect(() => {
        window.addEventListener('message', onReceiveMessage);
        return () => {
            window.removeEventListener('message', onReceiveMessage);
        };
    }, [onReceiveMessage]);

    useEffect(() => {
        if (waitingStartSessionFromChildren) {
            if (isClientConnect) {
                const token = UrlService.getSearchParamValue('token');
                startSessionConnect(token);
            } else if (device === ClientDevice.Browser) {
                startSessionWithParams(clientSessionParams);
            }
        }
    }, [isClientConnect, clientSessionParams, waitingStartSessionFromChildren, device, startSessionWithParams, startSessionConnect]);

    useEffect(() => {
        textChatPortalNode.element.className = 'vs-text-chat-portal-container';
    }, [textChatPortalNode]);

    return (
        <Div100vh style={{width: '100%', position: 'fixed'}}>
            {renderTextChatPortal()}
            {currentView === ClientView.SIP && <SipView />}
            {currentView === ClientView.FullScreenTextChat && <FullScreenTextChat TextChatComponent={renderTextChat} />}
            {currentView === ClientView.Calling && <CallingView />}
            {currentView === ClientView.WaitingForAgent && <WaitingForAgentView />}
            {currentView === ClientView.InSession && <InSessionView TextChatComponent={renderTextChat} />}
            {currentView === ClientView.OnHold && <OnHoldView />}
            {currentView === ClientView.SessionEnded && <SessionEndedView />}
            {currentView === ClientView.AgentsUnavailable && <AgentsUnavailableView />}
            {currentView === ClientView.AfterHours && <AfterHoursView />}
            {currentView === ClientView.RequestAppointment && <RequestAppointmentView />}
            {currentView === ClientView.Avatar && <AvatarView />}
            {currentView === ClientView.Loading && <Loader svgClassName='tw-max-w-12 tw-max-h-12' />}
            {currentView === ClientView.FullScreenAsyncRequest && <FullScreenAsyncRequest />}
            {currentView === ClientView.PreSessionFlow && <PreSessionFlowView />}
        </Div100vh>
    );
};

export const ClientConferenceWeb = withClientSessionParamsFromUrl()<Props>((props: Props) => {
    const device = useMemo(() => ClientDevice.Browser, []);
    const clientNotifier = useMemo(() => new ParentWindowNotifier(), []);
    return (
        <ClientSessionStartupContainer
            device={device}
            clientNotifier={clientNotifier}
            conversationOnly={props.conversationOnly}
        >
            <ClientConferenceWebContent {...props} />
        </ClientSessionStartupContainer>
    );
});
